import { useForm } from "@tanstack/react-form";
import { useMutation as useReactQueryMutation } from "@tanstack/react-query";
import { useMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { Button } from "../../components/ui/button";
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "../../components/ui/dialog";
import {
	Field,
	FieldDescription,
	FieldGroup,
	FieldLabel,
} from "../../components/ui/field";
import { Input } from "../../components/ui/input";

export default function AddPreRegisteredUserDialog({
	tenantId,
	open,
	onOpenChange,
}: {
	tenantId: Id<"tenants">;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}) {
	const addPreRegistered = useMutation(api.staff.addPreRegisteredUser);

	const form = useForm({
		defaultValues: { firstName: "", lastName: "", email: "" },
		onSubmit: async ({ value }) => {
			if (
				!value.firstName.trim() ||
				!value.lastName.trim() ||
				!value.email.trim()
			)
				return;
			mutate({
				firstName: value.firstName.trim(),
				lastName: value.lastName.trim(),
				email: value.email.trim(),
			});
		},
	});

	const { mutate, isPending, error } = useReactQueryMutation({
		mutationFn: async (args: {
			firstName: string;
			lastName: string;
			email: string;
		}) => {
			return await addPreRegistered({
				tenantId,
				firstName: args.firstName,
				lastName: args.lastName,
				email: args.email,
			});
		},
		onSuccess: () => {
			form.reset();
			onOpenChange(false);
		},
	});

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Pre-register user</DialogTitle>
					<DialogDescription>
						They can sign in with Google or Microsoft using this email and will
						automatically join this firm.
					</DialogDescription>
				</DialogHeader>
				<form
					onSubmit={(e) => {
						e.preventDefault();
						form.handleSubmit();
					}}
				>
					<FieldGroup>
						<div className="grid grid-cols-2 gap-3">
							<form.Field name="firstName">
								{(field) => (
									<Field>
										<FieldLabel htmlFor="pre-first-name">First name</FieldLabel>
										<Input
											id="pre-first-name"
											value={field.state.value}
											onChange={(e) => field.handleChange(e.target.value)}
											onBlur={field.handleBlur}
											required
											autoFocus
										/>
									</Field>
								)}
							</form.Field>
							<form.Field name="lastName">
								{(field) => (
									<Field>
										<FieldLabel htmlFor="pre-last-name">Last name</FieldLabel>
										<Input
											id="pre-last-name"
											value={field.state.value}
											onChange={(e) => field.handleChange(e.target.value)}
											onBlur={field.handleBlur}
											required
										/>
									</Field>
								)}
							</form.Field>
						</div>
						<form.Field name="email">
							{(field) => (
								<Field>
									<FieldLabel htmlFor="pre-email">Email</FieldLabel>
									<Input
										id="pre-email"
										type="email"
										value={field.state.value}
										onChange={(e) => field.handleChange(e.target.value)}
										onBlur={field.handleBlur}
										required
									/>
									<FieldDescription>
										They must sign in with this exact email via Google or
										Microsoft.
									</FieldDescription>
								</Field>
							)}
						</form.Field>
						{error ? (
							<Field>
								<p className="text-sm text-destructive">
									{error instanceof Error ? error.message : String(error)}
								</p>
							</Field>
						) : null}
						<DialogFooter>
							<DialogClose render={<Button variant="outline" type="button" />}>
								Cancel
							</DialogClose>
							<form.Subscribe
								selector={(state) => [
									state.values.firstName,
									state.values.lastName,
									state.values.email,
								]}
							>
								{([firstName, lastName, email]) => (
									<Button
										type="submit"
										disabled={
											isPending ||
											!(firstName as string).trim() ||
											!(lastName as string).trim() ||
											!(email as string).trim()
										}
									>
										{isPending ? "Adding..." : "Pre-register"}
									</Button>
								)}
							</form.Subscribe>
						</DialogFooter>
					</FieldGroup>
				</form>
			</DialogContent>
		</Dialog>
	);
}
